import { useEffect, useState } from "react";
import dayjs from "dayjs";

import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

import NodiantCalendar from "../../../../calendar/NodiantCalendar";

const toDate = (value) => (value ? dayjs(value).toDate() : null);

const formatRange = (range) => {
    if (!range) {
        return "All dates — pick a day to start a range.";
    }

    const [start, end] = range;

    return dayjs(start).isSame(dayjs(end), "day")
        ? dayjs(start).format("D MMM YYYY")
        : `${dayjs(start).format("D MMM YYYY")} – ${dayjs(end).format(
              "D MMM YYYY",
          )}`;
};

export default function HubUsageDateFilterDialog({
    endDate,
    onClose,
    open,
    startDate,
}) {
    const [range, setRange] = useState(null);

    // Reopening the filter should show the range that is actually applied.
    useEffect(() => {
        if (open) {
            setRange(
                startDate && endDate
                    ? [toDate(startDate), toDate(endDate)]
                    : null,
            );
        }
    }, [open, startDate, endDate]);

    const handleApply = () => {
        if (!range) {
            onClose(null, null);
            return;
        }

        onClose(dayjs(range[0]).startOf("day"), dayjs(range[1]).endOf("day"));
    };

    const handleClear = () => {
        setRange(null);
        onClose(null, null);
    };

    return (
        <Dialog
            onClose={handleApply}
            open={open}
            fullWidth
            maxWidth="sm"
            aria-labelledby="hub-usage-date-filter-title"
            slotProps={{
                paper: { sx: { borderRadius: 3, overflow: "hidden" } },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: "action.hover",
                    }}
                >
                    <CalendarMonthOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="hub-usage-date-filter-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        Date filter
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        Choose the activity date range.
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close date filter"
                    onClick={handleApply}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 1.5, sm: 2.5 },
                    backgroundColor: "background.default",
                }}
            >
                <Box
                    sx={{
                        p: { xs: 1, sm: 1.5 },
                        borderRadius: 2.5,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                    }}
                >
                    <NodiantCalendar
                        selectRange
                        selectedDate={range}
                        onDateChange={setRange}
                    />
                </Box>
                <Typography
                    variant="body2"
                    aria-live="polite"
                    sx={{ mt: 1.5, px: 0.5, color: "text.secondary" }}
                >
                    {formatRange(range)}
                </Typography>
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, gap: 1 }}>
                <Button
                    color="inherit"
                    onClick={handleClear}
                    disabled={!range && !startDate && !endDate}
                >
                    Clear dates
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={handleApply}
                >
                    Apply dates
                </Button>
            </DialogActions>
        </Dialog>
    );
}
